import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ArrowLeft, FlaskConical, Trash2, RefreshCw, LogIn, LogOut, Coffee, CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Test-punch chart & manager panel.
 * URL: /test-punches
 * Shows every kiosk punch made in TEST mode (via /kiosk-test).
 * Data lives in `test_time_punches` — totally isolated from real payroll.
 */
export default function TestPunchesPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/kiosk-face/test-punches?days=${days}`);
      setData(res.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [days]);

  const wipe = async () => {
    if (!window.confirm("Delete ALL test punches? This does NOT touch real payroll data.")) return;
    setBusy(true);
    try {
      const res = await axios.delete(`${API}/kiosk-face/test-punches`);
      toast.success(`Deleted ${res.data.deleted} test punches`);
      load();
    } catch (e) { toast.error("Wipe failed"); }
    finally { setBusy(false); }
  };

  const icon = { in: LogIn, out: LogOut, break_start: Coffee, break_end: Coffee };
  const color = { in: "text-emerald-400", out: "text-red-400", break_start: "text-amber-400", break_end: "text-blue-400" };

  return (
    <div className="min-h-screen bg-[#0F1013] text-white">
      <div className="bg-amber-400 text-black py-1.5 text-center font-black uppercase tracking-widest text-xs">
        🧪 TEST PUNCH SANDBOX — isolated from real payroll
      </div>
      <header className="border-b border-white/10 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <Link to="/timecard" className="text-white/70 hover:text-white flex items-center gap-1 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <h1 className="font-black text-lg flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-amber-300" /> Test Punches
        </h1>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            data-testid="test-days"
            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm">
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <Button size="sm" variant="ghost" onClick={load} data-testid="test-refresh"><RefreshCw className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={wipe} disabled={busy}
            className="border-red-400/50 text-red-300 hover:bg-red-500/10"
            data-testid="test-wipe">
            <Trash2 className="w-4 h-4 mr-1" /> Wipe All
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Stat label="Total punches" value={data?.count ?? 0} />
          <Stat label="Window" value={`${days}d`} />
          <Stat label="Live at" value="/kiosk-test" mono />
        </div>

        {!data?.punches?.length ? (
          <Card className="bg-white/5 border-white/10 p-10 text-center text-white/60">
            <FlaskConical className="w-10 h-10 mx-auto mb-2 opacity-40" />
            No test punches yet. Go to <Link to="/kiosk-test" className="text-amber-300 underline">/kiosk-test</Link> and try one.
          </Card>
        ) : (
          <Card className="bg-white/5 border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/10 text-white/70 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-left px-3 py-2">Date · Time</th>
                  <th className="text-right px-3 py-2">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {data.punches.map((p, i) => {
                  const Icon = icon[p.type] || CheckCircle2;
                  return (
                    <tr key={i} className="border-t border-white/5 text-white">
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 font-bold ${color[p.type] || "text-white"}`}>
                          <Icon className="w-4 h-4" /> {p.type}
                        </span>
                      </td>
                      <td className="px-3 py-2">#{p.employee_code}</td>
                      <td className="px-3 py-2 text-white/70 text-xs">{p.date} · {p.time}</td>
                      <td className="px-3 py-2 text-right font-mono">{p.face_confidence ?? "—"}/10</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}

        <p className="text-white/50 text-xs mt-4">
          Every punch on <b>/kiosk-test</b> lands here. None of these punches touch schedule, metrics, or payroll totals.
          Click <b>Wipe All</b> to reset the sandbox any time.
        </p>
      </main>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="text-[10px] uppercase tracking-wider text-white/60 font-bold">{label}</div>
      <div className={`text-xl font-black mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
